import { Trash2, Copy, GripVertical, ChevronUp, ChevronDown, Bookmark, ClipboardPaste } from 'lucide-react'
import { useBuilder } from '../context/BuilderContext'
import BlockRenderer from './BlockRenderer'
import { useSaveBlock } from './SavedBlocks'
import { useSortable } from '@dnd-kit/sortable'
import { useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'

const BLOCK_LABELS = {
  text: 'Text',
  heading: 'Heading',
  image: 'Image',
  button: 'Button',
  columns: 'Columns',
  menu: 'Menu',
  divider: 'Divider',
  spacer: 'Spacer',
  html: 'HTML',
  social: 'Social',
  video: 'Video',
  table: 'Table'
}

function SortableBlock({ block, isLast, isFirst }) {
  const { selectedBlockId, setSelectedBlockId, deleteBlock, duplicateBlock, moveBlockUp, moveBlockDown } = useBuilder()
  const { saveBlock } = useSaveBlock()
  const isSelected = selectedBlockId === block.id

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver
  } = useSortable({ id: block.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 999 : 'auto'
  }

  const showDropIndicatorBefore = isOver && !isDragging && !isLast
  const showDropIndicatorAfter = isOver && !isDragging && isLast

  return (
    <>
      {showDropIndicatorBefore && (
        <div style={{
          height: '4px',
          backgroundColor: 'var(--color-primary)',
          borderRadius: '2px',
          marginBottom: '0.5rem',
          boxShadow: '0 0 8px rgba(99, 102, 241, 0.5)',
          animation: 'pulse 1.5s ease-in-out infinite'
        }} />
      )}

      <div
        ref={setNodeRef}
        className="builder-sortable-item builder-block-wrapper"
        style={{
          ...style,
          position: 'relative',
          border: isSelected ? '2px solid var(--color-primary)' : '1px solid transparent',
          borderRadius: 'var(--radius-md)',
          marginBottom: '0.25rem',
          cursor: 'pointer',
          backgroundColor: 'transparent',
          boxShadow: isSelected ? '0 4px 12px rgba(99, 102, 241, 0.15)' : 'none',
          transition: 'all 0.2s'
        }}
        onClick={(e) => {
          e.stopPropagation()
          setSelectedBlockId(block.id)
        }}
        onMouseOver={(e) => {
          if (!isSelected) {
            e.currentTarget.style.border = '1px solid #c7d2fe'
          }
          // Show block label
          const label = e.currentTarget.querySelector('.block-type-label')
          if (label) label.style.opacity = '1'
        }}
        onMouseOut={(e) => {
          if (!isSelected) {
            e.currentTarget.style.border = '1px solid transparent'
          }
          const label = e.currentTarget.querySelector('.block-type-label')
          if (label && !isSelected) label.style.opacity = '0'
        }}
      >
        {/* Block Type Label */}
        <div className="block-type-label" style={{
          position: 'absolute',
          top: '-10px',
          left: '10px',
          background: isSelected ? 'var(--color-primary)' : '#94a3b8',
          color: '#fff',
          fontSize: '0.6rem',
          fontWeight: '600',
          padding: '1px 8px',
          borderRadius: '4px',
          zIndex: 5,
          opacity: isSelected ? 1 : 0,
          transition: 'opacity 0.15s',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          pointerEvents: 'none'
        }}>
          {BLOCK_LABELS[block.type] || block.type}
        </div>

        {/* Actions */}
        <div className="builder-block-actions" style={{
          position: 'absolute',
          top: '-12px',
          right: '8px',
          display: 'flex',
          gap: '0.25rem',
          zIndex: 10,
          opacity: isSelected ? 1 : 0,
          transition: 'opacity 0.2s'
        }}>
          <button
            onClick={(e) => { e.stopPropagation(); moveBlockUp(block.id) }}
            disabled={isFirst}
            title="Move up"
            style={{
              background: 'white', border: '1px solid #cbd5e0', borderRadius: '5px',
              padding: '0.25rem', display: 'flex', alignItems: 'center', cursor: isFirst ? 'default' : 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', opacity: isFirst ? 0.4 : 1
            }}
          >
            <ChevronUp size={13} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); moveBlockDown(block.id) }}
            disabled={isLast}
            title="Move down"
            style={{
              background: 'white', border: '1px solid #cbd5e0', borderRadius: '5px',
              padding: '0.25rem', display: 'flex', alignItems: 'center', cursor: isLast ? 'default' : 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', opacity: isLast ? 0.4 : 1
            }}
          >
            <ChevronDown size={13} />
          </button>
          <button
            {...attributes}
            {...listeners}
            className="builder-drag-handle"
            title="Drag to reorder"
            style={{
              background: 'white', border: '1px solid #cbd5e0', borderRadius: '5px',
              padding: '0.25rem 0.35rem', display: 'flex', alignItems: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'grab'
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <GripVertical size={13} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              duplicateBlock(block.id)
            }}
            title="Duplicate"
            style={{
              background: 'white', border: '1px solid #cbd5e0', borderRadius: '5px',
              padding: '0.25rem', display: 'flex', alignItems: 'center', cursor: 'pointer',
              color: '#0284c7', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            <Copy size={13} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              const blockData = JSON.stringify({ type: block.type, settings: block.settings })
              navigator.clipboard.writeText(blockData).then(() => {
                alert('Block copied to clipboard!')
              }).catch(() => {
                alert('Failed to copy')
              })
            }}
            title="Copy to clipboard"
            style={{
              background: 'white', border: '1px solid #cbd5e0', borderRadius: '5px',
              padding: '0.25rem', display: 'flex', alignItems: 'center', cursor: 'pointer',
              color: '#059669', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            <ClipboardPaste size={13} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              const name = prompt('Name this saved block:', `${block.type} block`)
              if (name) {
                saveBlock(block, name)
                alert('Block saved! Find it in the Toolbox under "Saved Blocks".')
              }
            }}
            title="Save block for reuse"
            style={{
              background: 'white', border: '1px solid #cbd5e0', borderRadius: '5px',
              padding: '0.25rem', display: 'flex', alignItems: 'center', cursor: 'pointer',
              color: '#8b5cf6', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            <Bookmark size={13} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (confirm('Delete this block?')) deleteBlock(block.id)
            }}
            title="Delete"
            style={{
              background: 'white', border: '1px solid #cbd5e0', borderRadius: '5px',
              padding: '0.25rem', display: 'flex', alignItems: 'center', cursor: 'pointer',
              color: '#dc2626', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            <Trash2 size={13} />
          </button>
        </div>

        <BlockRenderer block={block} />
      </div>

      {showDropIndicatorAfter && (
        <div style={{
          height: '4px',
          backgroundColor: 'var(--color-primary)',
          borderRadius: '2px',
          marginTop: '0.5rem',
          boxShadow: '0 0 8px rgba(99, 102, 241, 0.5)',
          animation: 'pulse 1.5s ease-in-out infinite'
        }} />
      )}
    </>
  )
}

export default function Canvas({ previewMode }) {
  const { blocks, setSelectedBlockId, bodySettings } = useBuilder()
  const { setNodeRef, isOver } = useDroppable({
    id: 'canvas-droppable'
  })

  const bs = bodySettings || {}
  const isDark = previewMode === 'dark'

  return (
    <div
      onClick={() => setSelectedBlockId(null)}
      style={{
        flex: 1,
        backgroundColor: isDark ? '#1a1a1a' : (bs.backgroundColor || '#f1f5f9'),
        padding: '1.5rem',
        overflowY: 'auto',
        transition: 'background-color 0.3s'
      }}
    >
      {isDark && (
        <div style={{
          textAlign: 'center',
          marginBottom: '0.75rem',
          padding: '0.4rem 0.75rem',
          background: 'rgba(251,191,36,0.1)',
          border: '1px solid rgba(251,191,36,0.2)',
          borderRadius: '8px',
          maxWidth: `${bs.emailWidth || 620}px`,
          margin: '0 auto 0.75rem'
        }}>
          <span style={{ fontSize: '0.75rem', color: '#fbbf24', fontWeight: '500' }}>
            Dark Mode Preview — Simulates how email clients render in dark mode
          </span>
        </div>
      )}
      <div
        ref={setNodeRef}
        style={{
          maxWidth: previewMode === 'mobile' ? '320px' : `${bs.emailWidth || 620}px`,
          margin: '0 auto',
          backgroundColor: isDark ? '#121212' : (bs.contentBackgroundColor || '#ffffff'),
          minHeight: '500px',
          borderRadius: bs.contentBorderRadius || '0px',
          boxShadow: previewMode === 'mobile'
            ? '0 0 0 8px #1e293b, 0 8px 32px rgba(0,0,0,0.18)'
            : isDark
              ? '0 4px 24px rgba(0,0,0,0.4)'
              : '0 1px 4px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
          padding: bs.contentPadding ? `${bs.contentPadding}%` : '0',
          border: isOver ? '2px dashed var(--color-primary)' : previewMode === 'mobile' ? '2px solid #1e293b' : isDark ? '1px solid #333' : '1px solid #e5e7eb',
          transition: 'border 0.2s, max-width 0.3s, background-color 0.3s, box-shadow 0.3s',
          filter: isDark ? 'invert(0.88) hue-rotate(180deg)' : 'none',
          borderTopLeftRadius: previewMode === 'mobile' ? '24px' : (bs.contentBorderRadius || '0px'),
          borderTopRightRadius: previewMode === 'mobile' ? '24px' : (bs.contentBorderRadius || '0px'),
          borderBottomLeftRadius: previewMode === 'mobile' ? '24px' : (bs.contentBorderRadius || '0px'),
          borderBottomRightRadius: previewMode === 'mobile' ? '24px' : (bs.contentBorderRadius || '0px'),
        }}
      >
        {blocks.length === 0 ? (
          <div style={{
            padding: '5rem 2rem',
            textAlign: 'center',
            color: '#94a3b8',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '0.25rem'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
            </div>
            <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '500', color: '#64748b' }}>
              Click on components from the left to start building your email
            </p>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>
              Or drag and drop them onto the canvas
            </p>
          </div>
        ) : (
          <>
            {blocks.map((block, index) => (
              <SortableBlock
                key={block.id}
                block={block}
                isFirst={index === 0}
                isLast={index === blocks.length - 1}
              />
            ))}

            {isOver && blocks.length > 0 && (
              <div style={{
                height: '4px',
                backgroundColor: 'var(--color-primary)',
                borderRadius: '2px',
                marginTop: '0.5rem',
                boxShadow: '0 0 8px rgba(99, 102, 241, 0.5)',
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />
            )}
          </>
        )}
      </div>
    </div>
  )
}
